import { apiUrl } from "../utils/api";

/**
 * Archivierte Mail-Dateien (`.msg` / `.eml`), die als KMVS-Dokument liegen.
 *
 * Spiegel des Backend-`ParsedMailFile` (apps/api/src/mail-file/mail-file.types.ts).
 * Bewusst NICHT der Graph-`Email`-Typ: eine Archivdatei hat kein Postfach, keinen
 * Ordner, keinen Thread und keinen Lesestatus.
 */
export interface MailFileAddress {
    name: string;
    address: string;
}

export interface MailFileAttachment {
    /** Position in der Anhangliste — dient als Id im Download-Endpunkt. */
    index: number;
    name: string;
    contentType: string;
    size: number;
    /** Im Body referenziert (Inline-Bild) statt echter Dateianhang. */
    isInline: boolean;
    contentId: string | null;
    /** Eingebettete Mail (`.msg` in `.msg`, `message/rfc822`). */
    isMail: boolean;
}

export interface ParsedMailFile {
    renderState?: "ready" | "limited" | "encrypted" | "unavailable";
    diagnostics?: Array<{ code: string; message: string }>;
    inlineParts?: Array<{ index: number; contentId: string; contentType: string; size: number }>;
    subject: string;
    from: MailFileAddress | null;
    to: MailFileAddress[];
    cc: MailFileAddress[];
    bcc: MailFileAddress[];
    date: string | null;
    /** Serverseitig sanitisiert; kleine Inline-Bilder stecken als `data:` drin. */
    bodyHtml: string;
    bodyWasHtml: boolean;
    attachments: MailFileAttachment[];
    /** Nicht-fatale Auffälligkeiten, die angezeigt werden sollen. */
    warnings: string[];
}

export interface MailFileAttachmentImportResult {
    documentId: number | null;
    /** `duplicate` = war schon übernommen. */
    /** `linked` (T-18): Datei lag schon beim Kunden, im Vorgang verknüpft statt kopiert. */
    status: "created" | "duplicate" | "linked";
}

export const mailFileService = {
    async fetchParsed(documentId: number): Promise<ParsedMailFile> {
        const res = await fetch(apiUrl(`documents/${documentId}/mail`));
        if (!res.ok) throw new Error(`Mail konnte nicht gelesen werden: ${res.status}`);
        return res.json();
    },

    /**
     * Anhang als Blob. Der Endpunkt liefert immer `Content-Disposition: attachment`;
     * die Anzeige (PDF/Bild) baut der Aufrufer selbst aus dem Blob.
     */
    async fetchAttachmentBlob(documentId: number, index: number): Promise<Blob> {
        const res = await fetch(apiUrl(`documents/${documentId}/mail/attachments/${index}`));
        if (!res.ok) throw new Error(`Anhang konnte nicht geladen werden: ${res.status}`);
        return res.blob();
    },

    /**
     * Anhang als eigenständiges Dokument in die Akte übernehmen — erbt die
     * Zuordnung der Quell-Mail (Kunde, Vorgang, …). Idempotent: ein zweiter
     * Aufruf liefert `duplicate`, ohne etwas anzulegen.
     */
    async importAttachment(documentId: number, index: number): Promise<MailFileAttachmentImportResult> {
        const res = await fetch(apiUrl(`documents/${documentId}/mail/attachments/${index}/import`), {
            method: "POST",
        });
        if (!res.ok) throw new Error(`Übernahme fehlgeschlagen: ${res.status}`);
        return res.json();
    },
};
